import { createContext, useContext, useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { AccountInfo, InteractionStatus, IPublicClientApplication } from "@azure/msal-browser";
import { loginRequest } from "../configs/authConfig";
import { User } from "../types/User";
import { useNavigate } from "react-router-dom";

interface AuthContextType {
  user: User | null;
  authLoading: boolean;
  unauthorized: boolean;
  instance: IPublicClientApplication;
  account: AccountInfo;
  isSupervisor: boolean | null;
  isFinanceMember: boolean | null;
  isFeedbackManager: boolean | null;
}

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { instance, accounts, inProgress } = useMsal();

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);
  const [isSupervisor, setIsSupervisor] = useState<boolean | null>(null);
  const [isFinanceMember, setIsFinanceMember] = useState<boolean | null>(null);
  const [isFeedbackManager, setIsFeedbackManager] = useState<boolean | null>(null);

  useEffect(() => {
    async function init() {
      setLoading(true);
      if (inProgress !== InteractionStatus.None) return;
      if (accounts.length === 0) {
        await instance.loginRedirect(loginRequest);
        return;
      }
      const account = accounts[0];

      const entraUserId = account.localAccountId;

      const token = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      });

      try {
        const response = await fetch(backendUrl + `/users/${entraUserId}`, {
          headers: {
            Authorization: `Bearer ${token.accessToken}`,
          },
        });

        if (!response.ok) {
          setUnauthorized(true);
          return;
        }

        const backendUser = await response.json();

        setUser({
          id: backendUser.id,
          roles: backendUser.roles,
          hasNotificationsOn: backendUser.hasNotificationsOn,
          entraUserId: entraUserId,
        });
        setUnauthorized(false);
      } catch {
        setUnauthorized(true);
      } finally {
        setLoading(false);
      }
    }
    init();
  }, [accounts, instance, inProgress, unauthorized]);

  useEffect(() => {
    if (unauthorized && window.location.pathname !== "/") {
      window.location.href = "/";
    }
  }, [unauthorized]);

  useEffect(() => {
    setLoading(true);
    if (!user?.roles) return;
    setIsFinanceMember(user.roles.some((r) => r.id === 2));
    setIsSupervisor(user.roles.some((r) => r.id === 1));
    setIsFeedbackManager(user.roles.some((r) => r.id === 3));
    setLoading(false);
  }, [user?.roles]);

  return (
    <AuthContext.Provider
      value={{
        user,
        authLoading: loading,
        unauthorized,
        instance,
        account: accounts[0],
        isSupervisor,
        isFinanceMember,
        isFeedbackManager,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

export async function getAccessToken(
  instance: IPublicClientApplication,
  account: AccountInfo,
): Promise<string> {
  const response = await instance.acquireTokenSilent({
    ...loginRequest,
    account,
  });

  return response.accessToken;
}

export function useRequireRole(role: boolean | null) {
  const { authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading || role === null) return;
    if (!role) navigate("/unauthorized");
  }, [navigate, authLoading, role]);
}
